import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getArticlesByName, getStockBreaks } from "../../../api/Services";
import {
  hasPermission,
  privilegesEnum,
} from "../../../helpers/PermissionHelper";

import { Table, Select, Typography, Spin } from "antd";

import styles from "./StockBreaks.module.scss";

const StockBreaks = () => {
  const { t } = useTranslation();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(1);
  const [articleOptions, setArticleOptions] = useState<any[]>([]);
  const [articleIdFilterValue, setArticleIdFilterValue] = useState("");

  const handlePagination = (page: number) => {
    handleFetchStockBreaks(page);
    setActualPage(page);
  };

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_REPOSITION_ORDERS)) {
      handleFetchStockBreaks();
    }
  }, []);

  const handleFetchStockBreaks = async (
    pagination = actualPage,
    filters?: {
      articleId?: string;
      branchOfficeId?: string;
    }
  ) => {
    setLoading(true);
    const response = await getStockBreaks(pagination - 1, {
      articleId: filters?.articleId ?? articleIdFilterValue,
    });

    setData(response.data);
    setTotalSize(response.totalElements);
    setLoading(false);
  };

  const handleSearchArticles = async (value: string) => {
    if (value.length > 2) {
      setLoadingArticles(true);
      const response = await getArticlesByName(value);
      const options = response.articles.map((item: any) => ({
        value: item.id,
        text: item.name,
      }));
      setArticleOptions(options);
      setLoadingArticles(false);
    } else setArticleOptions([]);
  };

  const columns = [
    {
      title: t("Common-Article"),
      key: "article",
      render: (record: any) => (
        <a href={`/admin/articles/${record.sku.articleId}/view`}>
          {record.sku.articleName}
        </a>
      ),
    },
    {
      title: t("Common-Sku"),
      key: "sku",
      render: (record: any) => record.sku.name,
    },
    {
      title: t("Common-Stock"),
      key: "stock",
      render: (record: any) => (
        <div className={styles.stockWrapper}>
          <Typography.Text>{`${record.sku.totalStock} ${t(
            "Common-StockCurrent"
          )}`}</Typography.Text>
          <Typography.Text>{`${record.sku.quantityInRepositionOrders} ${t(
            "Common-StockInTransit"
          )}`}</Typography.Text>
        </div>
      ),
    },
    {
      title: t("Common-EstimatedStockBreak"),
      dataIndex: "stockoutDate",
    },
    {
      title: t("Common-RepositionOrder"),
      key: "repositionOrder",
      render: (record: any) =>
        record.repositionOrder.length === 0
          ? t("Common-Pending")
          : record.repositionOrder.map((order: any) => (
              <div key={order.id}>
                <a href={`/repositionorders/${order.id}/view`}>
                  {order.orderStatus.description_es}
                </a>
              </div>
            )),
    },
    {
      title: t("Common-Quantity"),
      key: "quantity",
      render: (data: any) =>
        data.repositionOrder?.map((order: any) => {
          const sku = order.skus?.find((sku: any) => sku.skuId === data.sku.id);
          return <div key={order.id}>{sku ? sku.quantity : 0}</div>;
        }),
    },
    {
      title: t("Common-Delivery"),
      key: "delivery",
      render: (data: any) =>
        data.repositionOrder.map((order: any) => (
          <div key={order.id}>{order.estimatedDeliveryDate ?? "-"}</div>
        )),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("Common-StockBreaks")}</h1>
      </div>
      <div className={styles.contentWrapper}>
        {hasPermission(privilegesEnum.READ_REPOSITION_ORDERS) && (
          <div className={styles.tableWrapper}>
            <div className={styles.headerTableWrapper}>
              <div className={styles.filtersWrapper}>
                <div className={styles.filterWrapper}>
                  <Select
                    showSearch
                    placeholder={t("Common-SearchByArticle")}
                    allowClear
                    onClear={() => {
                      setArticleIdFilterValue("");
                      handleFetchStockBreaks(1, { articleId: "" });
                    }}
                    onSelect={(_, option) => {
                      if (option) {
                        setArticleIdFilterValue(option.value);
                        handleFetchStockBreaks(1, { articleId: option.value });
                      }
                    }}
                    notFoundContent={
                      loadingArticles ? <Spin size="small" /> : null
                    }
                    className={styles.filterSelect}
                    defaultActiveFirstOption={false}
                    showArrow={false}
                    filterOption={false}
                    onSearch={handleSearchArticles}
                    options={(articleOptions || []).map((d: any) => ({
                      value: d.value,
                      label: d.text,
                    }))}
                  />
                </div>
              </div>
            </div>
            <Table
              size="small"
              className={styles.table}
              loading={loading}
              columns={columns}
              dataSource={data}
              rowClassName={styles.row}
              rowKey={(record) => record.sku.id}
              pagination={{
                total: totalSize,
                showSizeChanger: false,
                onChange: handlePagination,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default StockBreaks;
